/**
 * 모듈 합성(머지) 커맨드 (일감 S2c 후반) — GameScene performModuleMerge 추출 (Phaser-free).
 * 검증(canMerge — 같은 이름·등급 3개, 미배치)·합성(mergeModules)·인벤토리 갱신만,
 * 결과 모듈 정보 팝업은 셸 콜백(openModuleInfo가 ModalHost/moduleMergeTab 강결합이라 잔존).
 * 컨트롤러는 ctx getter 경유 (applySaveData 교체 stale 방지 규약).
 */
import { canMerge, mergeModules } from "../../systems/ModuleMerge.js";
import type { ModuleInstance } from "../../models/ModuleInstance.js";
import type { GameContext } from "../GameContext.js";
import type { ShellPorts } from "./ports.js";

export interface MergeCommandDeps {
  ctx: GameContext;
  ports: ShellPorts;
  /** 합성 성공 직후 결과 모듈 정보 팝업 — 전투 그리드 탭과 동일 팝업 (요구사항, 셸 잔존). */
  onMergeResult: (instanceId: string) => void;
  /** 합성 성공 알림 — 가이드퀘스트 actionCompleted 목표 진행. */
  onActionCompleted?: (target: string) => void;
}

export class MergeCommands {
  constructor(private readonly deps: MergeCommandDeps) {}

  /**
   * 같은 이름·같은 등급 3개 → 다음 등급 1개. 재료가 누락/배치 중이거나 조건 불충족이면 합성 없이 UI만 최신화.
   * AutoPlay/MergeTabView가 호출하는 시그니처 유지.
   */
  merge(materialIds: readonly string[]): void {
    const { ctx, ports } = this.deps;
    const inv = ctx.inventory();
    const materials = materialIds
      .map((id) => inv.getInstance(id))
      .filter((i): i is ModuleInstance => !!i);
    const placed = new Set(ctx.grid().placedInstanceIds());
    const defOf = (id: string) => ctx.moduleRegistry().getDefinition(id);
    if (
      materials.length !== materialIds.length ||
      materials.some((m) => placed.has(m.instanceId)) ||
      !canMerge(materials, defOf)
    ) {
      ports.invalidateUi(); // stale — 그리드/슬롯만 최신화하고 합성하지 않음
      return;
    }
    const { merged, consumedIds } = mergeModules(materials, defOf, ctx.idSource()());
    for (const id of consumedIds) inv.removeInstance(id);
    inv.addInstance(merged);
    ctx.soundController().play("levelup");
    this.deps.onActionCompleted?.("module.merge");
    ports.persist();
    ports.invalidateUi();
    this.deps.onMergeResult(merged.instanceId);
  }
}
